import { useState, useRef, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { GALLERY } from "@/lib/siteData";

export default function Gallery() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    dragFree: false,
  });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIdx(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const lightboxPrev = () =>
    setLightboxIdx((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const lightboxNext = () => setLightboxIdx((i) => (i + 1) % GALLERY.length);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") lightboxPrev();
      if (e.key === "ArrowRight") lightboxNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx]);

  return (
    <section
      id="gallery"
      className="py-24 md:py-32 bg-foreground text-background"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p
              className="eyebrow"
              style={{ color: "hsl(var(--secondary))" }}
              data-testid="gallery-overline"
            >
              Gallery
            </p>
            <h2
              className="mt-4 font-headings text-3xl md:text-5xl text-background leading-tight"
              data-testid="gallery-heading"
            >
              Moments from our halls.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Previous slide"
              onClick={scrollPrev}
              className="w-11 h-11 rounded-sm border border-background/30 hover:border-secondary hover:text-secondary text-background flex items-center justify-center transition-colors"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              onClick={scrollNext}
              className="w-11 h-11 rounded-sm border border-background/30 hover:border-secondary hover:text-secondary text-background flex items-center justify-center transition-colors"
              data-testid="gallery-next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef} data-testid="gallery-carousel">
          <div className="flex gap-4 md:gap-6">
            {GALLERY.map((img, i) => (
              <button
                type="button"
                key={img.src}
                onClick={() => openLightbox(i)}
                className="flex-[0_0_85%] sm:flex-[0_0_55%] md:flex-[0_0_42%] lg:flex-[0_0_32%] relative group focus:outline-none"
                data-testid={`gallery-slide-${i}`}
              >
                <div className="overflow-hidden h-[340px] md:h-[440px] bg-foreground/40">
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover img-hover"
                  />
                </div>
                <p className="mt-3 font-ui text-[11px] uppercase tracking-[0.22em] text-background/70">
                  {String(i + 1).padStart(2, "0")} · {img.alt.split(/[—,]/)[0]}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2" aria-hidden="true">
          {GALLERY.map((_, i) => (
            <span
              key={i}
              className={`h-[2px] transition-all ${
                i === selectedIdx ? "w-10 bg-secondary" : "w-5 bg-background/25"
              }`}
            />
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-foreground/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-background/80 hover:text-secondary"
            aria-label="Close lightbox"
            data-testid="gallery-lightbox-close"
          >
            <X size={28} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              lightboxPrev();
            }}
            className="absolute left-4 md:left-8 text-background/80 hover:text-secondary p-2"
            aria-label="Previous image"
            data-testid="gallery-lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
          <div className="max-w-5xl w-full max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY[lightboxIdx].src}
              alt={GALLERY[lightboxIdx].alt}
              className="w-full h-full max-h-[85vh] object-contain"
            />
            <p className="mt-4 font-body text-center text-background/80">
              {GALLERY[lightboxIdx].alt}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              lightboxNext();
            }}
            className="absolute right-4 md:right-8 text-background/80 hover:text-secondary p-2"
            aria-label="Next image"
            data-testid="gallery-lightbox-next"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </section>
  );
}
